<template>
  <v-row v-bind="$attrs">
    <v-col v-if="inactiveService" cols="12">
      <hosting-pending :status="hosting.status" />
    </v-col>

    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); gap: 24px; width: 100%;" class="pt-4 pl-3 pr-3">
      <!-- Accounts -->
      <thin-card
        v-if="showAccounts"
        title="Account Usage"
        :text="accountsQty"
        icon="mdi-account-multiple"
      >
        <template v-slot:append>
          <v-progress-circular
            :size="50"
            :rotate="-90"
            :width="7"
            :value="accountsQtyPercent"
            color="primary"
          >
            {{ accountsQtyPercent }}%
          </v-progress-circular>
        </template>
      </thin-card>
      <!-- Disk Space Usage -->
      <thin-card
        title="Disk Space Usage"
        :text="spaceUsed"
        icon="mdi-harddisk"
      >
        <template v-slot:append>
          <v-progress-circular
            :size="50"
            :rotate="-90"
            :width="7"
            :value="spaceUsedPercent"
            color="primary"
          >
            {{ spaceUsedPercent }}%
          </v-progress-circular>
        </template>
      </thin-card>

      <!-- Bandwidth Usage -->
      <thin-card
        title="Bandwidth Usage"
        :text="bandwidthUsage"
        icon="mdi-upload"
      >
        <template v-slot:append>
          <v-progress-circular
            :size="50"
            :rotate="-90"
            :width="7"
            :value="bandwidthPercent"
            color="primary"
          >
            {{ bandwidthPercent }}%
          </v-progress-circular>
        </template>
      </thin-card>

      <!-- Inode Usage -->
      <thin-card
        style="flex: 0 0 calc((100% - 3 * 24px) / 4)"
        title="Inode Usage"
        :text="inodeUsed"
        icon="mdi-chart-bar"
      >
        <template v-slot:append>
          <v-progress-circular
            :size="50"
            :rotate="-90"
            :width="7"
            :value="inodeUsedPercent"
            color="primary"
          >
            {{ inodeUsedPercent }}%
          </v-progress-circular>
        </template>
      </thin-card>
    </div>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex'
import { humanFileSize, percent, launch_whm } from '@/helpers/helpers'
import HostingPending from '@/components/Services/Hosting/Common/HostingPending'
import ThinCard from '../../../Cards/ThinCard.vue'
import { SERVICE_TYPES } from '@/consts/service'

export default {
  name: 'ServiceLimits',
  props: {
    loading: {
      type: Boolean,
      required: true,
      default: false
    },
    showAccounts: {
      type: Boolean,
      default: false,
    }
  },
  components: {
    ThinCard,
    HostingPending
  },
  computed: {
    ...mapGetters(['hosting', 'hosting_detail']),
    inactiveService () {
      return this.hosting.status === 'Pending' || this.hosting.status === 'Suspended'
    },
    isResellerService () {
      return this.hosting.type === SERVICE_TYPES.RESELLER
    },
    isResellerSubAccount () {
      return this.isResellerService && this.$route.query.user
    },
    isResellerMainAccount () {
      return this.isResellerService && this.$route.query.user === this.hosting.username
    },
    accountData () {
      const data = {
        user: this.hosting_detail?.accountsummary?.user || '',
        server_hostname: this.hosting_detail?.accountsummary?.server_hostname || '',
        ip: this.hosting_detail?.accountsummary?.ip || '',
        diskused: this.hosting_detail?.accountsummary?.diskused || 0,
        disklimit: this.hosting_detail?.accountsummary?.disklimit || 0,
        inodesused: this.hosting_detail?.accountsummary?.inodesused || 0,
        inodeslimit: this.hosting_detail?.accountsummary?.inodeslimit || 0,
        bw_limit: this.hosting_detail?.showbw?.limit || 0,
        bw_used: this.hosting_detail?.showbw?.totalbytes || 0,
        accountsUsed: this.hosting_detail.acct_used || 0,
        accountsLimit: this.hosting_detail.acct_limit || 0,
      }
      if (this.isResellerService && (this.isResellerMainAccount || !this.isResellerSubAccount)) {
        data.user = this.hosting.username || ''
        data.server_hostname = this.hosting_detail?.hostname || ''
        data.ip = this.hosting_detail?.ip || ''
        data.diskused = this.hosting_detail?.disk_used || 0
        data.disklimit = this.hosting_detail?.disk_limit || 0
        data.inodesused = this.hosting_detail?.inodesused || 0
        data.inodeslimit = this.hosting_detail?.inodeslimit || 0
        data.bw_limit = this.hosting_detail?.bw_limit || 0
        data.bw_used = this.hosting_detail?.bw_used || 0
      }
      return data
    },
    accountsQtyPercent () {
      return this.percent(this.accountData.accountsUsed, this.accountData.accountsLimit - 0)
    },
    accountsQty () {
      let value = this.accountData.accountsUsed
      if (this.accountData.accountsLimit) {
        value += ` / ${this.accountData.accountsLimit}`
      }
      return value
    },
    spaceUsedPercent () {
      return this.percent(this.accountData.diskused, this.accountData.disklimit)
    },
    spaceUsed () {
      return `${this.humanFileSize(this.accountData.diskused)} / ${this.humanFileSize(this.accountData.disklimit)}`
    },
    inodeUsed () {
      return `${this.accountData.inodesused} / ${this.accountData.inodeslimit}`
    },
    inodeUsedPercent () {
      return this.percent(this.accountData.inodesused, this.accountData.inodeslimit)
    },
    bandwidthPercent () {
      return this.percent(this.accountData.bw_used, this.accountData.bw_limit)
    },
    bandwidthUsage () {
      return `${this.humanFileSize(this.accountData.bw_used)} / ${this.humanFileSize(this.accountData.bw_limit)}`
    }
  },
  methods: {
    humanFileSize,
    percent,
    launch_whm,
  },
}
</script>
